import firebase from "firebase/compat/app"; // Import compat for Auth providers
import { db, auth, getSecondaryApp, isLocalMode, setLocalMode } from './firebase'; // Removed 'storage' import
import { 
  collection as realCollection,
  getDocs as realGetDocs,
  doc as realDoc,
  setDoc as realSetDoc,
  getDoc as realGetDoc,
  deleteDoc as realDeleteDoc,
  query as realQuery,
  where as realWhere,
  limit as realLimit,
  writeBatch as realWriteBatch,
  updateDoc as realUpdateDoc,
  runTransaction as realRunTransaction,
  onSnapshot as realOnSnapshot,
  orderBy as realOrderBy
} from "firebase/firestore";

// --- Mock Firestore implementation for Local Storage / Offline resilient development mode ---
const isFirestoreDisabledError = (error: any): boolean => {
  if (!error) return false;
  const msg = (error.message || '').toLowerCase();
  const code = (error.code || '').toLowerCase();
  
  // Security rules errors or permission-denied errors should NEVER trigger local storage fallback.
  // Standard permission-denied indicates that Firestore is enabled and fully active, but the rule blocks access.
  if (code.includes('permission') || msg.includes('permission') || msg.includes('insufficient')) {
    return false;
  }
  
  return (
    msg.includes('firestore.googleapis.com') ||
    msg.includes('not been used in project') ||
    msg.includes('disabled') ||
    msg.includes('database was not found') ||
    msg.includes('does not exist') ||
    msg.includes('offline') ||
    msg.includes('failed to fetch') ||
    code.includes('unavailable') ||
    (code.includes('failed-precondition') && !msg.includes('permission'))
  );
};

const handleFirestoreFallback = (error?: any) => {
  if (!isLocalMode) {
    const errorStr = error ? String(error?.message || error).toLowerCase() : '';
    const isOffline = errorStr.includes('offline') || error?.code === 'unavailable' || errorStr.includes('fetch');
    if (isOffline) {
      console.log("No internet connection detected. Enabling offline local storage fallback.");
    } else {
      console.warn("Firestore is not enabled or available in this project. Falling back to local storage mode.");
    }
    setLocalMode(true);
  }
};
class MockDocRef {
  constructor(public collectionName: string, public id: string) {}
}
class MockCollectionRef {
  constructor(public collectionName: string) {}
}
class MockQuery {
  constructor(public collectionRef: MockCollectionRef, public constraints: any[]) {}
}

const getLocalData = (collectionName: string): any[] => {
  try {
    const data = localStorage.getItem(`db_${collectionName}`);
    return data ? JSON.parse(data) : [];
  } catch (e) {
    return [];
  }
};

const setLocalData = (collectionName: string, list: any[]) => {
  localStorage.setItem(`db_${collectionName}`, JSON.stringify(list));
};

const getLocalDoc = (collectionName: string, id: string): any => {
  const list = getLocalData(collectionName);
  return list.find(item => item.id === id) || null;
};

const setLocalDoc = (collectionName: string, id: string, data: any, merge: boolean = false) => {
  const list = getLocalData(collectionName);
  const idx = list.findIndex(item => item.id === id);
  const cleanData = JSON.parse(JSON.stringify(data));
  const finalDoc = merge && idx >= 0 ? { ...list[idx], ...cleanData, id } : { ...cleanData, id };
  if (idx >= 0) {
    list[idx] = finalDoc;
  } else {
    list.push(finalDoc);
  }
  setLocalData(collectionName, list);
};

const deleteLocalDoc = (collectionName: string, id: string) => {
  const list = getLocalData(collectionName);
  const filtered = list.filter(item => item.id !== id);
  setLocalData(collectionName, filtered);
};

export const doc = (databaseOrRef: any, collectionNameOrId?: string, ...paths: string[]): any => {
  if (isLocalMode) {
    if (databaseOrRef instanceof MockCollectionRef) {
      const id = collectionNameOrId || "mock_id_" + Math.random().toString(36).substring(2, 11);
      return new MockDocRef(databaseOrRef.collectionName, id);
    }
    const collectionName = collectionNameOrId || "";
    const id = paths.join('/') || "mock_id_" + Math.random().toString(36).substring(2, 11);
    return new MockDocRef(collectionName, id);
  }
  return realDoc(databaseOrRef, collectionNameOrId as string, ...paths);
};

export const collection = (database: any, collectionName: string): any => {
  if (isLocalMode) {
    return new MockCollectionRef(collectionName);
  }
  return realCollection(database, collectionName);
};

export const getDoc = async (docRef: any): Promise<any> => {
  if (isLocalMode && docRef instanceof MockDocRef) {
    const data = getLocalDoc(docRef.collectionName, docRef.id);
    return {
      exists: () => data !== null,
      id: docRef.id,
      ref: docRef,
      data: () => data
    };
  }
  try {
    return await realGetDoc(docRef);
  } catch (error: any) {
    if (isFirestoreDisabledError(error)) {
      handleFirestoreFallback();
      const pathParts = docRef.path ? docRef.path.split('/') : [];
      const collectionName = pathParts[0] || '';
      const id = pathParts.slice(1).join('/') || docRef.id;
      return getDoc(new MockDocRef(collectionName, id));
    }
    throw error;
  }
};

export const getDocs = async (queryOrRef: any): Promise<any> => {
  if (isLocalMode) {
    let collectionName = "";
    let constraints: any[] = [];
    if (queryOrRef instanceof MockCollectionRef) {
      collectionName = queryOrRef.collectionName;
    } else if (queryOrRef instanceof MockQuery) {
      collectionName = queryOrRef.collectionRef.collectionName;
      constraints = queryOrRef.constraints;
    }

    let list = getLocalData(collectionName);

    // Apply constraints
    for (const c of constraints) {
      if (c.type === 'where') {
        const { field, op, val } = c;
        list = list.filter(item => {
          const itemVal = item[field];
          if (op === '==') return itemVal === val;
          if (op === 'in') return Array.isArray(val) && val.includes(itemVal);
          return true;
        });
      } else if (c.type === 'limit') {
        list = list.slice(0, c.val);
      } else if (c.type === 'orderBy') {
        const { field, dir } = c;
        list.sort((a: any, b: any) => {
          const valA = a[field];
          const valB = b[field];
          if (valA === valB) return 0;
          if (valA === undefined || valA === null) return 1;
          if (valB === undefined || valB === null) return -1;
          const comparison = valA < valB ? -1 : 1;
          return dir === 'desc' ? -comparison : comparison;
        });
      }
    }

    return {
      docs: list.map(item => ({
        id: item.id,
        ref: new MockDocRef(collectionName, item.id),
        data: () => item
      }))
    };
  }
  try {
    return await realGetDocs(queryOrRef);
  } catch (error: any) {
    if (isFirestoreDisabledError(error)) {
      handleFirestoreFallback();
      let collectionName = 'surveys';
      if (queryOrRef.path) {
        collectionName = queryOrRef.path.split('/')[0];
      } else if (queryOrRef.collection?.path) {
        collectionName = queryOrRef.collection.path.split('/')[0];
      } else {
        const str = String(queryOrRef);
        if (str.includes('users')) collectionName = 'users';
        else if (str.includes('settings')) collectionName = 'settings';
      }
      return getDocs(new MockCollectionRef(collectionName));
    }
    throw error;
  }
};

export const query = (collectionRef: any, ...constraints: any[]): any => {
  if (isLocalMode) {
    if (collectionRef instanceof MockCollectionRef) {
      return new MockQuery(collectionRef, constraints);
    }
    if (collectionRef instanceof MockQuery) {
      return new MockQuery(collectionRef.collectionRef, [...collectionRef.constraints, ...constraints]);
    }
  }
  return realQuery(collectionRef, ...constraints);
};

export const where = (field: string, op: string, val: any): any => {
  if (isLocalMode) {
    return { type: 'where', field, op, val };
  }
  return realWhere(field, op as any, val);
};

export const limit = (val: number): any => {
  if (isLocalMode) {
    return { type: 'limit', val };
  }
  return realLimit(val);
};

export const orderBy = (field: string, dir: 'asc' | 'desc' = 'asc'): any => {
  if (isLocalMode) {
    return { type: 'orderBy', field, dir };
  }
  return realOrderBy(field, dir);
};

export const setDoc = async (docRef: any, data: any, options?: any): Promise<void> => {
  if (isLocalMode && docRef instanceof MockDocRef) {
    const merge = options && options.merge === true;
    setLocalDoc(docRef.collectionName, docRef.id, data, merge);
    return;
  }
  try {
    return await realSetDoc(docRef, data, options);
  } catch (error: any) {
    if (isFirestoreDisabledError(error)) {
      handleFirestoreFallback();
      const pathParts = docRef.path ? docRef.path.split('/') : [];
      const collectionName = pathParts[0] || '';
      const id = pathParts.slice(1).join('/') || docRef.id;
      return setDoc(new MockDocRef(collectionName, id), data, options);
    }
    throw error;
  }
};

export const updateDoc = async (docRef: any, data: any): Promise<void> => {
  if (isLocalMode && docRef instanceof MockDocRef) {
    setLocalDoc(docRef.collectionName, docRef.id, data, true);
    return;
  }
  try {
    return await realUpdateDoc(docRef, data);
  } catch (error: any) {
    if (isFirestoreDisabledError(error)) {
      handleFirestoreFallback();
      const pathParts = docRef.path ? docRef.path.split('/') : [];
      const collectionName = pathParts[0] || '';
      const id = pathParts.slice(1).join('/') || docRef.id;
      return updateDoc(new MockDocRef(collectionName, id), data);
    }
    throw error;
  }
};

export const deleteDoc = async (docRef: any): Promise<void> => {
  if (isLocalMode && docRef instanceof MockDocRef) {
    deleteLocalDoc(docRef.collectionName, docRef.id);
    return;
  }
  try {
    return await realDeleteDoc(docRef);
  } catch (error: any) {
    if (isFirestoreDisabledError(error)) {
      handleFirestoreFallback();
      const pathParts = docRef.path ? docRef.path.split('/') : [];
      const collectionName = pathParts[0] || '';
      const id = pathParts.slice(1).join('/') || docRef.id;
      return deleteDoc(new MockDocRef(collectionName, id));
    }
    throw error;
  }
};

class MockBatch {
  private operations: (() => void)[] = [];

  set(docRef: any, data: any, options?: any) {
    if (docRef instanceof MockDocRef) {
      const merge = options && options.merge === true;
      this.operations.push(() => setLocalDoc(docRef.collectionName, docRef.id, data, merge));
    } else if (docRef.path) {
      const pathParts = docRef.path.split('/');
      const collectionName = pathParts[0] || '';
      const id = pathParts.slice(1).join('/') || docRef.id;
      const merge = options && options.merge === true;
      this.operations.push(() => setLocalDoc(collectionName, id, data, merge));
    }
    return this;
  }

  update(docRef: any, data: any) {
    if (docRef instanceof MockDocRef) {
      this.operations.push(() => setLocalDoc(docRef.collectionName, docRef.id, data, true));
    } else if (docRef.path) {
      const pathParts = docRef.path.split('/');
      const collectionName = pathParts[0] || '';
      const id = pathParts.slice(1).join('/') || docRef.id;
      this.operations.push(() => setLocalDoc(collectionName, id, data, true));
    }
    return this;
  }

  delete(docRef: any) {
    if (docRef instanceof MockDocRef) {
      this.operations.push(() => deleteLocalDoc(docRef.collectionName, docRef.id));
    } else if (docRef.path) {
      const pathParts = docRef.path.split('/');
      const collectionName = pathParts[0] || '';
      const id = pathParts.slice(1).join('/') || docRef.id;
      this.operations.push(() => deleteLocalDoc(collectionName, id));
    }
    return this;
  }

  async commit() {
    this.operations.forEach(op => op());
  }
}

class BatchWrapper {
  private isFallback = false;
  private mockBatch: any = null;
  constructor(private realBatch: any, private database: any) {}

  set(docRef: any, data: any, options?: any) {
    if (this.isFallback || isLocalMode) {
      if (!this.mockBatch) this.mockBatch = new MockBatch();
      this.mockBatch.set(docRef, data, options);
    } else {
      try {
        this.realBatch.set(docRef, data, options);
      } catch (error: any) {
        if (isFirestoreDisabledError(error)) {
          this.isFallback = true;
          handleFirestoreFallback();
          if (!this.mockBatch) this.mockBatch = new MockBatch();
          this.mockBatch.set(docRef, data, options);
        } else {
          throw error;
        }
      }
    }
    return this;
  }

  update(docRef: any, data: any) {
    if (this.isFallback || isLocalMode) {
      if (!this.mockBatch) this.mockBatch = new MockBatch();
      this.mockBatch.update(docRef, data);
    } else {
      try {
        this.realBatch.update(docRef, data);
      } catch (error: any) {
        if (isFirestoreDisabledError(error)) {
          this.isFallback = true;
          handleFirestoreFallback();
          if (!this.mockBatch) this.mockBatch = new MockBatch();
          this.mockBatch.update(docRef, data);
        } else {
          throw error;
        }
      }
    }
    return this;
  }

  delete(docRef: any) {
    if (this.isFallback || isLocalMode) {
      if (!this.mockBatch) this.mockBatch = new MockBatch();
      this.mockBatch.delete(docRef);
    } else {
      try {
        this.realBatch.delete(docRef);
      } catch (error: any) {
        if (isFirestoreDisabledError(error)) {
          this.isFallback = true;
          handleFirestoreFallback();
          if (!this.mockBatch) this.mockBatch = new MockBatch();
          this.mockBatch.delete(docRef);
        } else {
          throw error;
        }
      }
    }
    return this;
  }

  async commit() {
    if (this.isFallback || isLocalMode) {
      if (this.mockBatch) await this.mockBatch.commit();
      return;
    }
    try {
      await this.realBatch.commit();
    } catch (error: any) {
      if (isFirestoreDisabledError(error)) {
        handleFirestoreFallback();
        if (this.mockBatch) {
          await this.mockBatch.commit();
        } else {
          console.warn("Real batch commit failed; fallback to MockMode.");
        }
      } else {
        throw error;
      }
    }
  }
}

export const writeBatch = (database: any): any => {
  if (isLocalMode) {
    return new MockBatch();
  }
  return new BatchWrapper(realWriteBatch(database), database);
};

export const runTransaction = async (database: any, callback: (transaction: any) => Promise<any>): Promise<any> => {
  if (isLocalMode) {
    const mockTransaction = {
      get: async (docRef: any) => {
        const pathParts = docRef instanceof MockDocRef ? [docRef.collectionName, docRef.id] : (docRef.path ? docRef.path.split('/') : []);
        const collectionName = pathParts[0] || '';
        const id = pathParts.slice(1).join('/') || docRef.id;
        const data = getLocalDoc(collectionName, id);
        return {
          exists: () => data !== null,
          id: id,
          ref: docRef instanceof MockDocRef ? docRef : new MockDocRef(collectionName, id),
          data: () => data
        };
      },
      set: (docRef: any, data: any, options?: any) => {
        const pathParts = docRef instanceof MockDocRef ? [docRef.collectionName, docRef.id] : (docRef.path ? docRef.path.split('/') : []);
        const collectionName = pathParts[0] || '';
        const id = pathParts.slice(1).join('/') || docRef.id;
        const merge = options && options.merge === true;
        setLocalDoc(collectionName, id, data, merge);
        return mockTransaction;
      },
      update: (docRef: any, data: any) => {
        const pathParts = docRef instanceof MockDocRef ? [docRef.collectionName, docRef.id] : (docRef.path ? docRef.path.split('/') : []);
        const collectionName = pathParts[0] || '';
        const id = pathParts.slice(1).join('/') || docRef.id;
        setLocalDoc(collectionName, id, data, true);
        return mockTransaction;
      },
      delete: (docRef: any) => {
        const pathParts = docRef instanceof MockDocRef ? [docRef.collectionName, docRef.id] : (docRef.path ? docRef.path.split('/') : []);
        const collectionName = pathParts[0] || '';
        const id = pathParts.slice(1).join('/') || docRef.id;
        deleteLocalDoc(collectionName, id);
        return mockTransaction;
      }
    };
    return await callback(mockTransaction);
  }
  try {
    return await realRunTransaction(database, callback);
  } catch (error: any) {
    if (isFirestoreDisabledError(error)) {
      handleFirestoreFallback();
      return runTransaction(database, callback);
    }
    throw error;
  }
};

export const onSnapshot = (queryOrRef: any, callback: (snapshot: any) => void, errorCallback?: (error: any) => void): any => {
  if (isLocalMode) {
    const runCheck = async () => {
      try {
        const snap = await getDocs(queryOrRef);
        callback(snap);
      } catch (err) {
        if (errorCallback) errorCallback(err);
      }
    };
    runCheck();
    const interval = setInterval(runCheck, 1500);
    return () => clearInterval(interval);
  }
  return realOnSnapshot(queryOrRef, callback, (err) => {
    if (isFirestoreDisabledError(err)) {
      handleFirestoreFallback();
      // Gracefully switch to interval polling in mock mode
      const runCheck = async () => {
        try {
          const snap = await getDocs(queryOrRef);
          callback(snap);
        } catch (error) {
          if (errorCallback) errorCallback(error);
        }
      };
      runCheck();
      const interval = setInterval(runCheck, 1500);
      return () => clearInterval(interval);
    }
    if (errorCallback) errorCallback(err);
  });
};

import { User, UserRole, SurveyData, CardTemplate, DynamicQuestion, SurveySection, BusJourney, BusStudent, StaffMember } from '../types';
import { INITIAL_QUESTIONS, INITIAL_SECTIONS } from '../constants';

const SURVEYS_COLLECTION = 'surveys';
const USERS_COLLECTION = 'users';
const SETTINGS_COLLECTION = 'settings';

// Helper to construct email from username
export const getEmail = (username: string) => {
  const clean = username.trim();
  if (clean.includes('@')) return clean;
  return `${clean.toLowerCase().replace(/\s/g, '')}@samajam.app`;
};

export const initStorage = async () => {
  // CRITICAL: If no user is logged in, Firestore rules will likely block this.
  // We check for currentUser. If null, we skip initialization unless in local storage mode.
  // It will be called again after login in App.tsx.
  if (!auth.currentUser && !isLocalMode) {
    console.log("Skipping storage initialization (Not logged in)");
    return;
  }

  try {
    // 1. Check Sections and layout ordering
    const sectionsDoc = await getDoc(doc(db, SETTINGS_COLLECTION, 'sections'));
    let forceReset = false;
    if (sectionsDoc.exists() && sectionsDoc.data()) {
      const list = sectionsDoc.data().list || [];
      const hasSectionC = list.some((s: any) => s.id === 'section_1782312497736');
      const isBFirst = list.length > 0 && list[0].id === 'section_1782312326957';
      if (hasSectionC || !isBFirst) {
        forceReset = true;
      }
    } else {
      forceReset = true;
    }

    if (forceReset) {
      console.log("Forcing layout update: removing section C, reordering Student Info first.");
      await setDoc(doc(db, SETTINGS_COLLECTION, 'sections'), { list: INITIAL_SECTIONS });
      await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: INITIAL_QUESTIONS });
    } else {
      // Just check if questions exist
      const questionsDoc = await getDoc(doc(db, SETTINGS_COLLECTION, 'questions'));
      if (!questionsDoc.exists() || (questionsDoc.data().list && questionsDoc.data().list.length === 0)) {
        await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: INITIAL_QUESTIONS });
      }
    }
  } catch (error: any) {
    // Gracefully handle permission errors if rules are strict
    const errorStr = String(error?.message || error).toLowerCase();
    const isOffline = errorStr.includes('offline') || error?.code === 'unavailable';
    if (isOffline) {
      console.log("Storage initialization: client is offline, using cached or default data.");
    } else if (error.code === 'permission-denied') {
      console.warn("Storage initialization skipped due to permissions. (Normal if basic user)");
    } else {
      console.warn("Storage initialization warning:", error);
    }
  }
};

// --- Authentication ---

export const createUserProfile = async (uid: string, username: string, name: string, role: UserRole): Promise<User> => {
  const user: User = {
    id: uid,
    username,
    name,
    role,
    visibleSections: ['sectionA', 'sectionB', 'sectionC', 'sectionD'],
    isActive: true
  };
  await setDoc(doc(db, USERS_COLLECTION, uid), user);
  return user;
};

export const registerMasterAdmin = async (name: string, username: string, password: string): Promise<User> => {
  const email = getEmail(username);
  try {
    const userCredential = await auth.createUserWithEmailAndPassword(email, password);
    const uid = userCredential.user?.uid;
    
    if (!uid) throw new Error("Failed to retrieve User ID from Firebase.");

    return await createUserProfile(uid, username, name, UserRole.MASTER_ADMIN);
  } catch (error: any) {
    if (error.code === 'auth/email-already-in-use') {
      throw new Error(`The account '${email}' already exists.`);
    }
    if (error.code === 'auth/weak-password') {
      throw new Error("Password should be at least 6 characters.");
    }
    throw new Error((error as any).message || "Failed to register Master Admin.");
  }
};

export const loginUser = async (username: string, password: string): Promise<User> => {
  const email = getEmail(username);
  try {
    const userCredential = await auth.signInWithEmailAndPassword(email, password);
    const uid = userCredential.user?.uid;
    
    if (!uid) throw new Error("User ID not found after login");

    try {
      const userDoc = await Promise.race([getDoc(doc(db, USERS_COLLECTION, uid)), new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000))]) as any;
      if (userDoc.exists()) {
        const profile = { id: uid, ...userDoc.data() } as User;
        try {
          localStorage.setItem(`cached_profile_${uid}`, JSON.stringify(profile));
        } catch (e) {
          console.warn("Failed to cache profile:", e);
        }
        
        // Block Deactivated Users
        if (profile.isActive === false) {
           await auth.signOut();
           throw new Error("Your account has been deactivated. Please contact the administrator.");
        }
        
        return profile;
      } else {
        throw new Error("Account exists in Auth, but user profile is missing in Database.");
      }
    } catch (dbError: any) {
      const errStr = String(dbError?.message || dbError).toLowerCase();
      const isOffline = errStr.includes('offline') || dbError?.code === 'unavailable' || errStr.includes('timeout');
      if (isOffline) {
        try {
          const cached = localStorage.getItem(`cached_profile_${uid}`);
          if (cached) {
            const profile = JSON.parse(cached) as User;
            if (profile.isActive === false) {
              throw new Error("Your account has been deactivated. Please contact the administrator.");
            }
            console.log("Offline login fallback: Loaded cached profile for", profile.name);
            return profile;
          }
        } catch (e) {
          console.warn("Failed to read cached profile:", e);
        }
        throw new Error("Login successful, but cannot fetch profile (Offline and no cached profile). Check internet connection.");
      }
      throw dbError;
    }
  } catch (error: any) {
    const msg = (error as any).message || '';
    if (msg.includes('auth/invalid-credential') || error.code === 'auth/wrong-password') {
      throw new Error(`Invalid credentials. Check username/password.`);
    }
    if (error.code === 'auth/network-request-failed') {
      throw new Error("Network error. Please check your connection.");
    }
    throw new Error(msg || "Login failed due to an unknown error.");
  }
};

export const logoutUser = async () => {
  await auth.signOut();
};

export const getCurrentUserProfile = async (uid: string): Promise<User | null> => {
  console.log("Getting profile for", uid);
  try {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, uid));
    if (userDoc.exists()) {
      const profile = { id: uid, ...userDoc.data() } as User;
      try {
        localStorage.setItem(`cached_profile_${uid}`, JSON.stringify(profile));
      } catch (e) {
        console.warn("Failed to cache profile in getCurrentUserProfile:", e);
      }
      return profile;
    }
  } catch (err: any) {
    const errStr = String(err?.message || err).toLowerCase();
    const isOffline = errStr.includes('offline') || err?.code === 'unavailable';
    if (isOffline) {
      console.log("Firestore is offline, checking offline profile cache for", uid);
    } else {
      console.warn("Error getting current user profile:", err);
    }
    
    try {
      const cached = localStorage.getItem(`cached_profile_${uid}`);
      if (cached) {
        return JSON.parse(cached) as User;
      }
    } catch (e) {
      console.warn("Failed to parse cached profile:", e);
    }
  }
  return null;
};

export const verifyUserCredentials = async (username: string, password: string) => {
  const currentUser = auth.currentUser;
  if (!currentUser || !currentUser.email) {
    throw new Error("No user is currently logged in.");
  }

  const inputEmail = getEmail(username);
  
  if (inputEmail.toLowerCase() !== currentUser.email.toLowerCase()) {
    throw new Error("Username does not match the currently logged-in user.");
  }

  try {
    const credential = firebase.auth.EmailAuthProvider.credential(currentUser.email, password);
    await currentUser.reauthenticateWithCredential(credential);
    return true;
  } catch (error: any) {
    if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
      throw new Error("Incorrect password.");
    }
    throw error;
  }
};

// --- Users Management ---

export const getUsers = async (): Promise<User[]> => {
  try {
    const snapshot = await getDocs(collection(db, USERS_COLLECTION));
    return snapshot.docs.map((d: any) => ({ id: d.id, ...d.data() } as User));
  } catch (error) {
    console.error("Failed to fetch users:", error);
    return [];
  }
};

export const saveUser = async (user: Omit<User, 'id'>, password: string) => {
  const secondaryApp = getSecondaryApp();
  const secondaryAuth = secondaryApp.auth();
  
  try {
    const email = getEmail(user.username);
    const userCredential = await secondaryAuth.createUserWithEmailAndPassword(email, password);
    const uid = userCredential.user?.uid;

    if (!uid) throw new Error("Failed to generate UID");

    await setDoc(doc(db, USERS_COLLECTION, uid), {
      username: user.username,
      name: user.name,
      role: user.role,
      isActive: true, // Default to true for new users
      visibleSections: user.visibleSections || ['sectionA', 'sectionB', 'sectionC', 'sectionD']
    });

    await secondaryAuth.signOut();
    await secondaryApp.delete();
  } catch (error: any) {
    console.error("Create User Error:", error);
    if (error.code === 'auth/email-already-in-use') {
      throw new Error("Username/Email already exists.");
    }
    throw new Error((error as any).message);
  }
};

export const updateUser = async (userId: string, data: Partial<User>) => {
  try {
    await updateDoc(doc(db, USERS_COLLECTION, userId), data);
  } catch (e: any) {
    throw new Error("Failed to update user profile: " + e.message);
  }
};

export const updateCurrentUserAuth = async (password: string) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Not logged in");
  if (password) await user.updatePassword(password);
};

export const deleteUser = async (userId: string) => {
  await deleteDoc(doc(db, USERS_COLLECTION, userId));
};

// --- Surveys ---

export const getSurveys = async (filters?: { userId?: string, limitCount?: number }): Promise<SurveyData[]> => {
  try {
    let q = query(collection(db, SURVEYS_COLLECTION));

    if (filters?.userId) {
      q = query(q, where('submittedBy', '==', filters.userId));
    }

    if (filters?.limitCount) {
      q = query(q, limit(filters.limitCount));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((d: any) => d.data() as SurveyData);
  } catch (error) {
    console.error("Failed to fetch surveys:", error);
    return [];
  }
};

// Robust Serial Number Generation
// Uses a dedicated metadata document to track the last serial number
// to prevent race conditions and inconsistent gaps.
export const getNextSerialNo = async (): Promise<string> => {
  try {
    let nextVal = 1;
    await runTransaction(db, async (transaction) => {
        const metaRef = doc(db, SETTINGS_COLLECTION, 'metadata');
        const metaSnap = await transaction.get(metaRef);
        
        if (metaSnap.exists() && typeof metaSnap.data().lastSerialNo === 'number') {
            nextVal = metaSnap.data().lastSerialNo + 1;
            transaction.set(metaRef, { lastSerialNo: nextVal }, { merge: true });
        } else {
            // Fallback
            nextVal = 1;
            transaction.set(metaRef, { lastSerialNo: nextVal }, { merge: true });
        }
    });
    return nextVal.toString().padStart(4, '0');
  } catch (e) {
    console.error("Serial generation failed:", e);
    // Emergency fallback
    return '0000-' + Math.floor(Math.random() * 10000);
  }
};

// Utility to fix existing inconsistent data
export const repairSerialNumbers = async (): Promise<string> => {
  try {
    const snapshot = await getDocs(collection(db, SURVEYS_COLLECTION));
    const surveys = snapshot.docs.map((d: any) => ({ id: d.id, ...d.data() } as SurveyData));
    
    // Sort by submission date (oldest first)
    // We want to keep the ID of the oldest entry in case of duplicates
    surveys.sort((a: any, b: any) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime());

    const seenSerials = new Set<string>();
    const seenMemberSerials = new Set<string>();
    const batch = writeBatch(db);
    let updatesCount = 0;
    
    // 1. Calculate the true maximum serial currently in existence
    let maxSerial = 0;
    let maxLS = 0;
    let maxCC = 0;
    let maxFL = 0;
    
    // Fetch dynamic sections and questions to do dynamic repeater updates
    const questions = await getDynamicQuestions();
    const sections = await getDynamicSections();
    const repeaterSecs = sections.filter(s => s.isRepeater);
    const repeaterIds = repeaterSecs.length > 0 ? repeaterSecs.map(s => s.id) : ['sectionB'];

    surveys.forEach((s: any) => {
       const currentStr = s.sectionA?.serialNo;
       const num = parseInt(currentStr || '0', 10);
       if (!isNaN(num) && num > maxSerial) maxSerial = num;

       const pSerial = s.sectionA?.studentSerialNo || s.studentSerialNo || '';
       if (pSerial.startsWith('LS-')) {
           const v = parseInt(pSerial.split('-')[1] || '0', 10);
           if (!isNaN(v) && v > maxLS) maxLS = v;
       } else if (pSerial.startsWith('CC-')) {
           const v = parseInt(pSerial.split('-')[1] || '0', 10);
           if (!isNaN(v) && v > maxCC) maxCC = v;
       } else if (pSerial.startsWith('FL-')) {
           const v = parseInt(pSerial.split('-')[1] || '0', 10);
           if (!isNaN(v) && v > maxFL) maxFL = v;
       }

       for (const rId of repeaterIds) {
           const members = s[rId];
           if (Array.isArray(members)) {
               members.forEach(m => {
                   const mSerial = m.serialNo || m.idNo || '';
                   if (mSerial.startsWith('LS-')) {
                       const v = parseInt(mSerial.split('-')[1] || '0', 10);
                       if (!isNaN(v) && v > maxLS) maxLS = v;
                   } else if (mSerial.startsWith('CC-')) {
                       const v = parseInt(mSerial.split('-')[1] || '0', 10);
                       if (!isNaN(v) && v > maxCC) maxCC = v;
                   } else if (mSerial.startsWith('FL-')) {
                       const v = parseInt(mSerial.split('-')[1] || '0', 10);
                       if (!isNaN(v) && v > maxFL) maxFL = v;
                   }
               });
           }
       }
    });

    console.log("Current detected max household serial:", maxSerial, "LS:", maxLS, "CC:", maxCC, "FL:", maxFL);

    // Get current category counters
    const metaRef = doc(db, SETTINGS_COLLECTION, 'metadata');
    const metaSnap = await getDoc(metaRef);
    const metaData = metaSnap.exists() ? metaSnap.data() : {};
    
    let lastLS = Math.max(metaData.lastLSNo || 0, maxLS);
    let lastCC = Math.max(metaData.lastCCNo || 0, maxCC);
    let lastFL = Math.max(metaData.lastFLNo || 0, maxFL);
    let countersChanged = false;

    // 2. Iterate and fix
    for (const survey of surveys) {
       const currentSerial = survey.sectionA?.serialNo;
       
       const isDuplicate = seenSerials.has(currentSerial);
       const isInvalid = !currentSerial || currentSerial === '0000' || isNaN(parseInt(currentSerial));

       let surveyNeedsUpdate = false;
       let updatedSectionA = { ...(survey.sectionA || {}) };

       if (isDuplicate || isInvalid) {
           // Assign new serial at the end of the chain
           maxSerial++;
           const newSerial = maxSerial.toString().padStart(4, '0');
           console.log(`Fixing survey ${survey.id} household serial. Old: ${currentSerial}, New: ${newSerial}`);
           updatedSectionA.serialNo = newSerial;
           seenSerials.add(newSerial);
           surveyNeedsUpdate = true;
           updatesCount++;
       } else {
           seenSerials.add(currentSerial);
       }

       // Also inspect members and repair any draft/missing serials!
       let updatedSurvey = { ...survey };
       
       const primaryName = getMemberName(updatedSurvey.sectionA || updatedSurvey, questions);
       const primaryAge = getMemberAge(updatedSurvey.sectionA || updatedSurvey, questions);
       const primaryCategory = getCategoryForAge(primaryAge);
       
       if (primaryName && primaryCategory) {
           const mainSource = updatedSurvey.sectionA ? updatedSurvey.sectionA : updatedSurvey;
           const currentPrimarySerial = mainSource.studentSerialNo || '';
           const isMemberDraft = !currentPrimarySerial || currentPrimarySerial.toLowerCase().includes('draft');
           const isMemberDup = currentPrimarySerial && seenMemberSerials.has(currentPrimarySerial);
           
           if (isMemberDraft || isMemberDup) {
              let nextNum = 0;
              if (primaryCategory === 'LS') {
                 lastLS++;
                 nextNum = lastLS;
              } else if (primaryCategory === 'CC') {
                 lastCC++;
                 nextNum = lastCC;
              } else if (primaryCategory === 'FL') {
                 lastFL++;
                 nextNum = lastFL;
              }
              const serialNo = `${primaryCategory}-${nextNum.toString().padStart(4, '0')}`;
              mainSource.studentSerialNo = serialNo;
              mainSource.studentIdNo = serialNo;
              seenMemberSerials.add(serialNo);
              surveyNeedsUpdate = true;
              countersChanged = true;
           } else if (currentPrimarySerial) {
              seenMemberSerials.add(currentPrimarySerial);
           }
       }

       for (const rId of repeaterIds) {
         const members = survey[rId];
         if (Array.isArray(members)) {
           let membersChanged = false;
           const updatedMembers = members.map((member) => {
             const name = getMemberName(member, questions);
             const age = getMemberAge(member, questions);
             const category = getCategoryForAge(age);
             
             if (name && category) {
               const currentMemberSerial = member.serialNo || '';
               const isMemberDraft = !currentMemberSerial || currentMemberSerial.toLowerCase().includes('draft');
               const isMemberDup = currentMemberSerial && seenMemberSerials.has(currentMemberSerial);

               if (isMemberDraft || isMemberDup) {
                 let nextNum = 0;
                 if (category === 'LS') {
                   lastLS++;
                   nextNum = lastLS;
                 } else if (category === 'CC') {
                   lastCC++;
                   nextNum = lastCC;
                 } else if (category === 'FL') {
                   lastFL++;
                   nextNum = lastFL;
                 }
                 
                 const serialNo = `${category}-${nextNum.toString().padStart(4, '0')}`;
                 membersChanged = true;
                 countersChanged = true;
                 console.log(`Fixing member ${name} in survey ${survey.id}. New ID: ${serialNo}`);
                 const newMember = {
                   ...member,
                   serialNo,
                   idNo: serialNo,
                   id_no: serialNo,
                   id: serialNo,
                   categoryName: getCategoryName(category)
                 };
                 seenMemberSerials.add(newMember.serialNo);
                 return newMember;
               }
             }
             if (member.serialNo) seenMemberSerials.add(member.serialNo);
             return member;
           });

           if (membersChanged) {
             updatedSurvey[rId] = updatedMembers;
             surveyNeedsUpdate = true;
             updatesCount++;
           }
         }
       }

       if (surveyNeedsUpdate) {
         const docRef = doc(db, SURVEYS_COLLECTION, survey.id);
         updatedSurvey.sectionA = updatedSectionA;
         batch.update(docRef, updatedSurvey);
       }
    }

    // 3. Update the global metadata counters
    const metadataUpdates: any = { 
        lastSerialNo: maxSerial,
        lastLSNo: lastLS,
        lastCCNo: lastCC,
        lastFLNo: lastFL
    };
    
    if (updatesCount > 0 || countersChanged) {
         batch.set(metaRef, metadataUpdates, { merge: true });
         await batch.commit();
         return `Repaired ${updatesCount} issues. Counters synchronized to max.`;
    } else {
         await setDoc(metaRef, metadataUpdates, { merge: true });
         return `No duplicate serials found. Counters synced to max values (Household: ${maxSerial}, LS: ${lastLS}, CC: ${lastCC}, FL: ${lastFL}).`;
    }

  } catch (error) {
     console.error("Repair error", error);
     throw new Error("Repair failed: " + (error as any).message);
  }
};

export const getCategoryForAge = (age: number): 'LS' | 'CC' | 'FL' | null => {
  if (isNaN(age) || age < 4) return null;
  if (age >= 4 && age <= 8) return 'LS';
  if (age >= 9 && age <= 12) return 'CC';
  if (age >= 13 && age <= 21) return 'FL'; // Student age cap at 21
  return null;
};

export const getCategoryName = (category: string | null): string => {
  if (category === 'LS') return 'Little Stars';
  if (category === 'CC') return 'Creative Champs';
  if (category === 'FL') return 'Future Leaders';
  return '';
};

export const getMemberAge = (member: any, questions: any[]): number => {
  if (!member || typeof member !== 'object') return NaN;
  
  // 1. Look for fields in questions
  for (const q of questions) {
    const val = member[q.id];
    if (val !== undefined && val !== null && val !== '') {
      const labelLower = String(q.label || '').toLowerCase();
      // Check if this is a DOB field
      if (
        labelLower.includes('dob') || 
        labelLower.includes('birth') || 
        labelLower.includes('ജനന') || 
        labelLower.includes('birthdate') || 
        labelLower.includes('തീയതി') || 
        labelLower.includes('date of birth') ||
        q.type === 'date'
      ) {
        const birthDate = new Date(val);
        if (!isNaN(birthDate.getTime())) {
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const m = today.getMonth() - birthDate.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          return age;
        }
      }
      // Check if this is an Age field
      if (
        labelLower.includes('age') || 
        labelLower.includes('വയസ്സ്') || 
        labelLower.includes('പ്രായം') || 
        labelLower.includes('വയസ്')
      ) {
        const ageNum = parseInt(String(val), 10);
        if (!isNaN(ageNum)) {
          return ageNum;
        }
      }
    }
  }
  
  // 2. Fallback: check raw object keys and values (e.g. if question keys are dynamic / numbers)
  for (const key of Object.keys(member)) {
    const val = member[key];
    if (val !== undefined && val !== null && val !== '') {
      // If the value matches standard HTML date picker format YYYY-MM-DD
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
        const birthDate = new Date(val);
        if (!isNaN(birthDate.getTime())) {
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const m = today.getMonth() - birthDate.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          if (age >= 0 && age <= 120) {
            return age;
          }
        }
      }
      
      const keyLower = key.toLowerCase();
      if (
        keyLower.includes('dob') || 
        keyLower.includes('birth') || 
        keyLower.includes('ജനന') || 
        keyLower.includes('birthdate') || 
        keyLower.includes('തീയതി') || 
        keyLower.includes('date of birth')
      ) {
        const birthDate = new Date(val);
        if (!isNaN(birthDate.getTime())) {
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const m = today.getMonth() - birthDate.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          return age;
        }
      } else if (
        keyLower.includes('age') || 
        keyLower.includes('വയസ്സ്') || 
        keyLower.includes('പ്രായം') || 
        keyLower.includes('വയസ്')
      ) {
        const ageNum = parseInt(String(val), 10);
        if (!isNaN(ageNum)) {
          return ageNum;
        }
      }
    }
  }
  
  return NaN;
};

export const getMemberName = (member: any, questions: any[]): string => {
  if (!member || typeof member !== 'object') return '';
  
  // 0.0 CHECK studentName or student_name explicitly first!
  const explicitKeys = ['studentName', 'student_name', 'childName', 'fullName', 'fullNameMalayalam', 'name', 'memberName'];
  for (const key of explicitKeys) {
    if (member[key] && typeof member[key] === 'string' && member[key].trim().length > 0) {
      return String(member[key]).trim();
    }
  }

  // Helper to identify keys or labels related to hobbies, schools, sports, and other non-person-name fields
  const isNonNameField = (str: string): boolean => {
    const s = str.toLowerCase();
    return s.includes('hobby') || s.includes('hobbies') || s.includes('school') || s.includes('സ്ഥാപനം') || s.includes('വിനോദം') || s.includes('കലകൾ') || s.includes('കായികം') || s.includes('ക്ലാസ്') || s.includes('class') || s.includes('standard') || s.includes('തൊഴിൽ') || 
           s.includes('parent') || s.includes('father') || s.includes('mother') || s.includes('guardian') || s.includes('email') ||
           s.includes('രക്ഷിതാവ്') || s.includes('രക്ഷിതാ') || s.includes('മാതാവ്') || s.includes('മാതാ') || s.includes('അമ്മ') || s.includes('ഉമ്മ') ||
           s.includes('പിതാവ്') || s.includes('പിതാ') || s.includes('അച്ഛൻ') || s.includes('ഉപ്പ') || s.includes('fathername') || s.includes('mothername') ||
           s.includes('football') || s.includes('cricket') || s.includes('drawing') || s.includes('painting') || s.includes('singing') || s.includes('music') || s.includes('reading') || s.includes('traveling') || s.includes('cooking') || s.includes('badminton') || s.includes('sports') || s.includes('gaming') || s.includes('dance') || s.includes('dancing') || s.includes('art') || s.includes('craft') ||
           s.includes('ഫുട്ബോൾ') || s.includes('ക്രിക്കറ്റ്') || s.includes('വരക്കൽ') || s.includes('പാട്ട്') || s.includes('പാട്ടുപാടൽ') || s.includes('വായന') || s.includes('പാചകം') || s.includes('കളി');
  };

  // 0. User explicitly requested: Use the 1st question in Section A that isn't the Mother or ID No.
  if (questions && Array.isArray(questions)) {
    const sectionAQs = questions.filter(q => q.section === 'sectionA' || !q.section);
    for (const q of sectionAQs) {
      if (q.type !== 'text') continue;
      const val = member[q.id];
      if (val && typeof val === 'string' && val.trim().length > 0) {
        const labelLower = String(q.label || '').toLowerCase();
        const idLower = String(q.id || '').toLowerCase();
        
        // Skip explicitly non-name fields
        if (isNonNameField(labelLower) || isNonNameField(idLower)) continue;
        
        const idNormalized = idLower.replace(/[.\s_-]/g, '');
        const labelNormalized = labelLower.replace(/[.\s_-]/g, '');

        // Skip ID and Address fields
        const isIdField = idNormalized === 'id' || 
                          idNormalized.includes('idno') || 
                          idNormalized.includes('serial') || 
                          idNormalized.includes('number') ||
                          idNormalized.includes('no') ||
                          idNormalized.includes('reg') ||
                          idNormalized.includes('admission') ||
                          idNormalized.includes('roll') ||
                          labelNormalized.includes('idno') || 
                          labelNormalized.includes('serial') || 
                          labelNormalized.includes('നമ്പർ') || 
                          labelNormalized.includes('number') ||
                          labelNormalized.includes('idno') ||
                          labelNormalized.includes('admission') ||
                          labelNormalized.includes('reg') ||
                          labelNormalized.includes('roll');

        const isAddressField = idNormalized.includes('house') || labelNormalized.includes('house') || labelNormalized.includes('വീട്') ||
                               idNormalized.includes('ward') || labelNormalized.includes('ward') || labelNormalized.includes('വാർഡ്') ||
                               idNormalized.includes('phone') || labelNormalized.includes('phone') || labelNormalized.includes('മൊബൈൽ') ||
                               idNormalized.includes('mobile') || labelNormalized.includes('mobile');

        if (isIdField || isAddressField) continue;

        // If it passes all the above, this is the first valid text question in Section A!
        return String(val).trim();
      }
    }
  }

  // 1. Try to find an EXPLICIT student name field in questions first (Highest Priority)
  if (questions && Array.isArray(questions)) {
    for (const q of questions) {
      const val = member[q.id];
      if (val !== undefined && val !== null && val !== '') {
        const labelLower = String(q.label || '').toLowerCase();
        const idLower = String(q.id || '').toLowerCase();
        
        if (isNonNameField(labelLower) || isNonNameField(idLower)) continue;

        if (
          (labelLower.includes('student') && labelLower.includes('name')) ||
          (labelLower.includes('child') && labelLower.includes('name')) ||
          labelLower.includes('വിദ്യാർത്ഥിയുടെ പേര്') ||
          labelLower.includes('കുട്ടിയുടെ പേര്') ||
          idLower.includes('studentname') ||
          idLower.includes('student_name') ||
          idLower.includes('student') ||
          idLower.includes('child')
        ) {
          return String(val).trim();
        }
      }
    }
  }

  // 2. Direct key priority (especially for student name over parent/hobbies)
  const priorityKeys = ['studentName', 'student_name', 'childName', 'fullName', 'fullNameMalayalam', 'name', 'memberName'];
  for (const key of priorityKeys) {
    if (member[key] && typeof member[key] === 'string' && member[key].trim().length > 0) {
      return String(member[key]).trim();
    }
  }

  // 3. Try to find a field in questions (PASS B: Any other person-name matching fields)
  if (questions && Array.isArray(questions)) {
    for (const q of questions) {
      const val = member[q.id];
      if (val !== undefined && val !== null && val !== '') {
        const labelLower = String(q.label || '').toLowerCase();
        const idLower = String(q.id || '').toLowerCase();

        if (isNonNameField(labelLower) || isNonNameField(idLower)) continue;

        if (
          labelLower.includes('name') || 
          labelLower.includes('പേര്') || 
          idLower.includes('name') ||
          labelLower.includes('member')
        ) {
          return String(val).trim();
        }
      }
    }
  }
  
  // 4. Fuzzy key search in the object itself
  const nameIndicators = ['name', 'പേര്', 'fullname', 'student', 'child'];
  for (const key of Object.keys(member)) {
    const keyLower = key.toLowerCase();
    if (isNonNameField(keyLower)) continue;
    
    if (nameIndicators.some(ind => keyLower.includes(ind))) {
      const val = member[key];
      if (val && typeof val === 'string' && val.trim().length > 0 && !val.startsWith('data:image')) {
        return String(val).trim();
      }
    }
  }

  // 4. Ultimate fallback: find the first non-ID, non-numeric, non-image string
  for (const key of Object.keys(member)) {
    const val = member[key];
    if (typeof val === 'string' && val.trim().length > 2) {
      const v = val.trim();
      if (
        !v.startsWith('data:image') && 
        !/^\d+$/.test(v) && 
        !key.toLowerCase().includes('id') &&
        !key.toLowerCase().includes('serial') &&
        !key.toLowerCase().includes('phone') &&
        !key.toLowerCase().includes('image') &&
        !key.toLowerCase().includes('photo') &&
        !key.toLowerCase().includes('email') &&
        !v.includes('@') &&
        !v.includes('.com') &&
        !v.includes('http') &&
        !isNonNameField(key) &&
        !isNonNameField(v) &&
        v.length < 100 // Exclude very long strings that might be blobs
      ) {
        return v;
      }
    }
  }
  
  return '';
};

export const getMemberPhone = (member: any, questions: any[]): string => {
  if (!member || typeof member !== 'object') return '';
  
  // 1. Direct keys priority
  const priorityKeys = ['phone', 'mobile', 'whatsapp', 'contact', 'tel', 'parentPhone', 'phoneNumber', 'mobileNumber'];
  for (const key of priorityKeys) {
    if (member[key]) return String(member[key]).trim();
  }

  // 2. Question-based check
  if (questions && Array.isArray(questions)) {
    for (const q of questions) {
      const val = member[q.id];
      if (val) {
        const labelLower = String(q.label || '').toLowerCase();
        const idLower = String(q.id).toLowerCase();
        if (
          labelLower.includes('phone') || 
          labelLower.includes('mobile') || 
          labelLower.includes('ഫോൺ') || 
          labelLower.includes('മൊബൈൽ') || 
          labelLower.includes('നമ്പർ') ||
          idLower.includes('phone') ||
          idLower.includes('mobile')
        ) {
          return String(val).trim();
        }
      }
    }
  }
  
  // 3. Fuzzy search
  for (const key of Object.keys(member)) {
    const keyLower = key.toLowerCase();
    if (keyLower.includes('phone') || keyLower.includes('mobile') || keyLower.includes('contact')) {
      return String(member[key]).trim();
    }
  }
  return '';
};

export const getMemberIICStatus = (member: any, questions: any[]): boolean => {
  if (!member || typeof member !== 'object') return false;
  
  // 1. Direct key check
  const priorityKeys = [
    'isIICMember', 'iicMember', 'membership', 'isMember', 'iic_member', 'memberStatus',
    'isSamajamMember', 'samajamMember', 'samajam_member'
  ];
  for (const key of priorityKeys) {
    const val = member[key];
    if (val === true || val === 'true' || val === 'Yes' || (typeof val === 'string' && val.toLowerCase() === 'true')) return true;
  }

  // 2. Question-based check
  if (questions && Array.isArray(questions)) {
    for (const q of questions) {
      const val = member[q.id];
      if (val !== undefined && val !== null && val !== '') {
        const labelLower = String(q.label || '').toLowerCase();
        const idLower = String(q.id).toLowerCase();
        if (
          labelLower.includes('iic') || 
          labelLower.includes('ഐ.ഐ.സി') || 
          labelLower.includes('samajam') ||
          labelLower.includes('സമാജം') ||
          labelLower.includes('centre member') || 
          labelLower.includes('സെന്റർ മെമ്പർ') ||
          labelLower.includes('മെമ്പർഷിപ്പ്') ||
          labelLower.includes('member') ||
          idLower.includes('iic') ||
          idLower.includes('samajam') ||
          idLower.includes('member')
        ) {
          if (val === true || val === 'true' || val === 'Yes' || (typeof val === 'string' && val.toLowerCase() === 'true')) return true;
          if (typeof val === 'string' && (val.toLowerCase().includes('yes') || val.includes('അതെ'))) return true;
        }
      }
    }
  }

  // 3. Fuzzy key search
  for (const key of Object.keys(member)) {
    const keyLower = key.toLowerCase();
    if (keyLower.includes('member') || keyLower.includes('iic') || keyLower.includes('samajam')) {
      const val = member[key];
      if (val === true || val === 'true' || val === 'Yes' || (typeof val === 'string' && val.toLowerCase() === 'true')) return true;
    }
  }

  return false;
};

export const getSurveyIICId = (formData: Record<string, any>, questions: any[]): string | null => {
  const searchInObject = (obj: any): string | null => {
    if (!obj || typeof obj !== 'object') return null;
    
    // Look for common membership ID keys
    const commonKeys = [
      'membershipId', 'memberId', 'iicId', 'iicNumber', 'membershipNumber', 
      'memberNumber', 'cardId', 'idNumber', 'iic_id', 'membership_id',
      'iicMemberId', 'iic_member_id', 'samajamId', 'samajamNumber', 'samajam_id', 'samajam_member_id'
    ];

    for (const key of Object.keys(obj)) {
      const keyLower = key.toLowerCase();
      if (commonKeys.some(ck => keyLower === ck.toLowerCase())) {
        return obj[key];
      }
    }

    // Fuzzy search
    for (const key of Object.keys(obj)) {
      const keyLower = key.toLowerCase();
      if ((keyLower.includes('iic') || keyLower.includes('samajam') || keyLower.includes('member')) && keyLower.includes('id')) {
        return obj[key];
      }
      if ((keyLower.includes('iic') || keyLower.includes('samajam') || keyLower.includes('member')) && keyLower.includes('no')) {
        return obj[key];
      }
    }

    // Question-based search
    if (questions && Array.isArray(questions)) {
      for (const q of questions) {
        if (obj[q.id] !== undefined && obj[q.id] !== null && obj[q.id] !== '') {
          const labelLower = String(q.label || '').toLowerCase();
          if ((labelLower.includes('iic') || labelLower.includes('samajam') || labelLower.includes('member')) && (labelLower.includes('id') || labelLower.includes('no') || labelLower.includes('number'))) {
            return obj[q.id];
          }
        }
      }
    }

    return null;
  };

  // 1. Check Section A (Household Head)
  const sectionA = formData.sectionA || formData['section_1782311875584'];
  if (sectionA) {
    const id = searchInObject(sectionA);
    if (id) return String(id);
  }

  // 2. Check all sections
  for (const sectionId of Object.keys(formData)) {
    const sectionData = formData[sectionId];
    if (Array.isArray(sectionData)) {
      for (const m of sectionData) {
        const id = searchInObject(m);
        if (id) return String(id);
      }
    } else {
      const id = searchInObject(sectionData);
      if (id) return String(id);
    }
  }

  return null;
};

export const getSurveyIICStatus = (formData: Record<string, any>, questions: any[], sections: any[]): boolean => {
  // 1. Check Section A (Head of Household)
  const sectionA = formData.sectionA || formData['section_1782311875584'];
  if (sectionA && getMemberIICStatus(sectionA, questions)) return true;
  
  // 2. Check all repeater sections
  const repeaterSections = sections.filter(s => s.isRepeater);
  for (const s of repeaterSections) {
    const members = formData[s.id] || [];
    if (members.some((m: any) => getMemberIICStatus(m, questions))) return true;
  }
  
  // 3. Fallback check all top-level sections
  for (const sectionId of Object.keys(formData)) {
    const sectionData = formData[sectionId];
    if (Array.isArray(sectionData)) {
      if (sectionData.some((m: any) => getMemberIICStatus(m, questions))) return true;
    } else if (typeof sectionData === 'object' && sectionData !== null) {
      if (getMemberIICStatus(sectionData, questions)) return true;
    }
  }
  
  return false;
};

export const getHouseholdName = (survey: any, questions: any[]): string => {
  if (!survey) return '';
  
  // 0. High priority explicit keys check
  const sectionAObj = survey.sectionA || {};
  const explicitParentKeys = ['parentName', 'parent_name', 'fatherName', 'father_name', 'guardianName', 'guardian_name'];
  for (const key of explicitParentKeys) {
    if (sectionAObj[key] && typeof sectionAObj[key] === 'string' && sectionAObj[key].trim().length > 0) {
      return String(sectionAObj[key]).trim();
    }
    if (survey[key] && typeof survey[key] === 'string' && survey[key].trim().length > 0) {
      return String(survey[key]).trim();
    }
  }

  // 1. Try to specifically find parent/guardian name based on questions
  if (questions && Array.isArray(questions)) {
    const parentQuestions = questions.filter(q => {
      const labelLower = String(q.label || '').toLowerCase();
      const idLower = String(q.id || '').toLowerCase();
      return labelLower.includes('parent') || labelLower.includes('guardian') || labelLower.includes('father') || labelLower.includes('രക്ഷിതാവ്') || labelLower.includes('രക്ഷിതാ') || idLower.includes('parent');
    });

    for (const q of parentQuestions) {
      // Check section A explicitly
      if (survey.sectionA && survey.sectionA[q.id]) return String(survey.sectionA[q.id]).trim();
      // Check root
      if (survey[q.id]) return String(survey[q.id]).trim();
      
      // Search recursively
      for (const key of Object.keys(survey)) {
        if (key === 'id' || key === 'submittedBy' || key === 'submittedAt') continue;
        const section = survey[key];
        if (section && typeof section === 'object' && !Array.isArray(section) && section[q.id]) {
          return String(section[q.id]).trim();
        }
      }
    }
  }

  // 2. Fallback to common keys explicitly for parent
  const sectionA = survey.sectionA || survey['section_1782311875584'] || {};
  if (sectionA.parentName) return String(sectionA.parentName).trim();
  if (sectionA.fatherName) return String(sectionA.fatherName).trim();
  if (sectionA.guardianName) return String(sectionA.guardianName).trim();
  
  if (survey.parentName) return String(survey.parentName).trim();
  if (survey.fatherName) return String(survey.fatherName).trim();
  if (survey.guardianName) return String(survey.guardianName).trim();

  // 3. Fallback to head name
  if (sectionA.headName) return String(sectionA.headName).trim();
  if (survey.headName) return String(survey.headName).trim();

  // 4. Scan all sections that are objects (robust search - try to extract ANY name)
  for (const key of Object.keys(survey)) {
    if (key === 'id' || key === 'submittedBy' || key === 'submittedAt') continue;
    const section = survey[key];
    if (section && typeof section === 'object' && !Array.isArray(section)) {
      if (section.parentName) return String(section.parentName).trim();
    }
  }
  
  return '';
};

export const getHouseholdPhone = (survey: any, questions: any[]): string => {
  if (!survey) return '';
  
  // 1. Check sectionA explicitly
  const sectionA = survey.sectionA || survey['section_1782311875584'] || {};
  if (sectionA.phone) return String(sectionA.phone).trim();
  
  // 2. Check root
  if (survey.phone) return String(survey.phone).trim();

  // 3. Scan all sections that are objects
  for (const key of Object.keys(survey)) {
    if (key === 'id' || key === 'submittedBy' || key === 'submittedAt') continue;
    const section = survey[key];
    if (section && typeof section === 'object' && !Array.isArray(section)) {
      const phone = getMemberPhone(section, questions);
      if (phone) return phone;
    }
  }

  // 4. Fallback to root scan
  return getMemberPhone(survey, questions) || '';
};

export const getHouseholdSerialNo = (survey: any): string => {
  if (!survey) return '';
  if (survey.sectionA && survey.sectionA.serialNo) return survey.sectionA.serialNo;
  if (survey.serialNo) return survey.serialNo;
  for (const key of Object.keys(survey)) {
    const val = survey[key];
    if (val && typeof val === 'object' && !Array.isArray(val) && val.serialNo) return val.serialNo;
  }
  return '';
};

export const getMemberPhoto = (member: any, questions: any[]): string => {
  if (!member || typeof member !== 'object') return '';
  
  // 1. Look for fields of type 'image' in questions
  const imageQuestions = questions.filter(q => q.type === 'image');
  for (const q of imageQuestions) {
    if (member[q.id] && typeof member[q.id] === 'string' && (member[q.id].startsWith('data:image') || member[q.id].startsWith('http'))) {
      return member[q.id];
    }
  }

  // 2. Fallback to common keys
  const standardKeys = ['housePhoto', 'photo', 'image', 'studentPhoto', 'memberPhoto', 'profilePhoto', 'house_photo'];
  for (const key of standardKeys) {
    if (member[key] && typeof member[key] === 'string' && (member[key].startsWith('data:image') || member[key].startsWith('http'))) {
      return member[key];
    }
  }

  // 3. Fallback to any string starting with data:image
  for (const key of Object.keys(member)) {
    if (typeof member[key] === 'string' && member[key].startsWith('data:image')) {
      return member[key];
    }
  }

  return '';
};

export const getHouseholdPhoto = (survey: any, questions: any[]): string => {
  if (!survey) return '';
  
  // 1. Check root
  const rootPhoto = getMemberPhoto(survey, questions);
  if (rootPhoto) return rootPhoto;

  // 2. Scan all sections
  for (const key of Object.keys(survey)) {
    if (key === 'id' || key === 'submittedBy' || key === 'submittedAt') continue;
    const section = survey[key];
    if (section && typeof section === 'object' && !Array.isArray(section)) {
      const photo = getMemberPhoto(section, questions);
      if (photo) return photo;
    }
  }

  return '';
};

const cleanUndefined = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(cleanUndefined);
  }
  const cleanObj: any = {};
  for (const key of Object.keys(obj)) {
    const val = obj[key];
    if (val !== undefined) {
      cleanObj[key] = cleanUndefined(val);
    }
  }
  return cleanObj;
};

export const saveSurvey = async (survey: SurveyData) => {
  let finalSurvey = cleanUndefined({ ...survey });
  
  // Keep legacy sectionA synchronized with the parent/guardian section 'section_1782311875584'
  const parentSecId = 'section_1782311875584';
  if (finalSurvey[parentSecId] && !finalSurvey.sectionA) {
    finalSurvey.sectionA = finalSurvey[parentSecId];
  } else if (finalSurvey.sectionA && !finalSurvey[parentSecId]) {
    finalSurvey[parentSecId] = finalSurvey.sectionA;
  }
  
  try {
    const questions = await getDynamicQuestions();
    const sections = await getDynamicSections();
    const repeaterSecs = sections.filter(s => s.isRepeater);
    const repeaterIds = repeaterSecs.length > 0 ? repeaterSecs.map(s => s.id) : ['sectionB'];

    await runTransaction(db, async (transaction) => {
      const metaRef = doc(db, SETTINGS_COLLECTION, 'metadata');
      const metaSnap = await transaction.get(metaRef);
      const metaData = metaSnap.exists() ? metaSnap.data() : {};
      
      let { lastLSNo: lastLS = 0, lastCCNo: lastCC = 0, lastFLNo: lastFL = 0, lastSerialNo: lastGlobal = 0 } = metaData;
      let updatedMetadata: Record<string, number> = {};
      let metadataChanged = false;
      
      // Also process the primary student in sectionA or flat data!
      const primaryName = getMemberName(finalSurvey.sectionA || finalSurvey, questions);
      const primaryAge = getMemberAge(finalSurvey.sectionA || finalSurvey, questions);
      const primaryCategory = getCategoryForAge(primaryAge);
      if (primaryName && primaryCategory) {
          const mainSource = finalSurvey.sectionA ? finalSurvey.sectionA : finalSurvey;
          // IMPORTANT: we only assign a student ID to 'studentSerialNo' or similar, 
          // because 'serialNo' at the root/sectionA is the Household Registration ID!
          const currentPrimarySerial = mainSource.studentSerialNo || '';
          if (!currentPrimarySerial || currentPrimarySerial.toLowerCase().includes('draft')) {
              let nextNum = 0;
              if (primaryCategory === 'LS') { nextNum = ++lastLS; updatedMetadata.lastLSNo = lastLS; }
              else if (primaryCategory === 'CC') { nextNum = ++lastCC; updatedMetadata.lastCCNo = lastCC; }
              else if (primaryCategory === 'FL') { nextNum = ++lastFL; updatedMetadata.lastFLNo = lastFL; }
              
              if (nextNum > 0) {
                  metadataChanged = true;
                  const newSerial = `${primaryCategory}-${nextNum.toString().padStart(4, '0')}`;
                  mainSource.studentSerialNo = newSerial;
                  mainSource.studentIdNo = newSerial;
              }
          }
      }

      for (const rId of repeaterIds) {
        const members = finalSurvey[rId];
        if (Array.isArray(members)) {
          finalSurvey[rId] = members.map((member: any) => {
            const name = getMemberName(member, questions);
            const age = getMemberAge(member, questions);
            const category = getCategoryForAge(age);
            
            if (name && category) {
              const currentSerial = member.serialNo || member.idNo || '';
              if (!currentSerial || currentSerial.toLowerCase().includes('draft')) {
                let nextNum = 0;
                if (category === 'LS') { nextNum = ++lastLS; updatedMetadata.lastLSNo = lastLS; }
                else if (category === 'CC') { nextNum = ++lastCC; updatedMetadata.lastCCNo = lastCC; }
                else if (category === 'FL') { nextNum = ++lastFL; updatedMetadata.lastFLNo = lastFL; }
                
                if (nextNum > 0) {
                  metadataChanged = true;
                  const newSerial = `${category}-${nextNum.toString().padStart(4, '0')}`;
                  return { 
                    ...member, 
                    serialNo: newSerial, 
                    idNo: newSerial,
                    id_no: newSerial,
                    id: newSerial,
                    categoryName: getCategoryName(category)
                  };
                }
              }
            }
            return member;
          });
        }
      }

      if (finalSurvey.sectionA?.serialNo) {
         const currentVal = parseInt(finalSurvey.sectionA.serialNo, 10);
         if (!isNaN(currentVal) && currentVal > lastGlobal) {
            updatedMetadata.lastSerialNo = currentVal;
            metadataChanged = true;
         }
      }

      const surveyRef = doc(db, SURVEYS_COLLECTION, finalSurvey.id);
      transaction.set(surveyRef, finalSurvey);
      
      if (metadataChanged) {
        transaction.set(metaRef, updatedMetadata, { merge: true });
      }
    });

    return finalSurvey;
  } catch (error) {
    console.error("Critical Save Error:", error);
    // Fallback save attempt
    await setDoc(doc(db, SURVEYS_COLLECTION, finalSurvey.id), finalSurvey);
    return finalSurvey;
  }
};

export const deleteSurvey = async (id: string) => {
  await deleteDoc(doc(db, SURVEYS_COLLECTION, id));
};

export const resetSurveys = async () => {
  const snapshot = await getDocs(collection(db, SURVEYS_COLLECTION));
  const batch = writeBatch(db);
  snapshot.docs.forEach((doc: any) => {
    batch.delete(doc.ref);
  });
  // Also reset metadata
  batch.set(doc(db, SETTINGS_COLLECTION, 'metadata'), { lastSerialNo: 0 });
  await batch.commit();
};

// --- Settings (Questions & Sections) ---

export const getDynamicSections = async (): Promise<SurveySection[]> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'sections'));
    if (docSnap.exists() && docSnap.data() && Array.isArray(docSnap.data().list)) {
      const list = docSnap.data().list as SurveySection[];
      // Filter out Section C
      const filtered = list.filter(s => s.id !== 'section_1782312497736');
      
      // Defensive mapping to ensure proper ordering and validation
      const validatedList = filtered.map((s, idx) => {
        let orderVal = s.order;
        if (s.id === 'section_1782312326957') { // Force Section B to be order 0
          orderVal = 0;
        } else if (s.id === 'section_1782311875584') { // Force Section A to be order 1
          orderVal = 1;
        } else if (typeof orderVal === 'string') {
          const parsed = parseInt(orderVal, 10);
          orderVal = isNaN(parsed) ? idx : parsed;
        } else if (typeof orderVal !== 'number') {
          orderVal = idx;
        }
        return {
          ...s,
          order: orderVal
        };
      });
      return validatedList.sort((a, b) => a.order - b.order);
    }
  } catch (error: any) {
    const errorStr = String(error?.message || error).toLowerCase();
    const isOffline = errorStr.includes('offline') || error?.code === 'unavailable';
    if (isOffline) {
      console.log("Firestore offline: fell back to local/cached sections.");
    } else {
      console.warn("Error fetching sections:", error);
    }
  }
  return INITIAL_SECTIONS;
}

export const saveSections = async (sections: SurveySection[]) => {
  const cleanSections = (sections || []).map((s, i) => ({
    ...s,
    order: i
  }));
  await setDoc(doc(db, SETTINGS_COLLECTION, 'sections'), { list: cleanSections });
};

export const getDynamicQuestions = async (section?: string): Promise<DynamicQuestion[]> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'questions'));
    if (docSnap.exists() && docSnap.data() && Array.isArray(docSnap.data().list)) {
      const all = docSnap.data().list as DynamicQuestion[];
      // Filter out Section C questions
      const filtered = all.filter(q => q.section !== 'section_1782312497736');
      if (section) return filtered.filter(q => q.section === section);
      return filtered;
    }
  } catch (error) {
    // console.warn("Error fetching questions (likely permissions):", error);
    // Don't warn loudly, just return defaults.
  }
  return section ? INITIAL_QUESTIONS.filter(q => q.section === section) : INITIAL_QUESTIONS;
};

export const saveDynamicQuestion = async (q: DynamicQuestion) => {
  const current = await getDynamicQuestions();
  const index = current.findIndex(item => item.id === q.id);
  
  let newList = [...current];
  if (index >= 0) {
    newList[index] = q;
  } else {
    newList.push(q);
  }
  
  await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: newList });
};

export const saveQuestionsOrder = async (questions: DynamicQuestion[]) => {
  await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: questions || [] });
};

export const deleteDynamicQuestion = async (id: string) => {
  const current = await getDynamicQuestions();
  const newList = current.filter(q => q.id !== id);
  await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: newList });
};

export const saveTemplate = async (template: CardTemplate) => {
  const dataToSave = { ...template };
  if (dataToSave.photoConfig === undefined) {
    dataToSave.photoConfig = null;
  }
  await setDoc(doc(db, SETTINGS_COLLECTION, 'template'), dataToSave);
};

export const getTemplate = async (): Promise<CardTemplate | null> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'template'));
    return docSnap.exists() ? docSnap.data() as CardTemplate : null;
  } catch (e: any) {
    const errorStr = String(e?.message || e).toLowerCase();
    const isOffline = errorStr.includes('offline') || e?.code === 'unavailable';
    if (isOffline) {
      console.log("Firestore offline: template fetch deferred.");
    } else {
      console.warn("Failed to get template:", e);
    }
    return null;
  }
};
export const getBusAssignments = async (driverId: string) => {
  const docRef = doc(db, 'bus_assignments', driverId);
  const snap = await getDoc(docRef);
  return snap.exists() ? snap.data().assignedStudents || [] : [];
};

export const saveBusAssignments = async (driverId: string, assignedStudents: any[]) => {
  const docRef = doc(db, 'bus_assignments', driverId);
  await setDoc(docRef, { assignedStudents }, { merge: true });
};

export const subscribeBusJourneys = (callback: (journeys: BusJourney[]) => void) => {
  const q = query(collection(db, 'bus_journeys'), orderBy('date', 'desc'));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() } as BusJourney)));
  });
};

export const subscribeDriverActiveJourney = (driverId: string, callback: (journey: BusJourney | null) => void) => {
  const q = query(collection(db, 'bus_journeys'), where('driverId', '==', driverId));
  return onSnapshot(q, (snapshot) => {
    const activeDocs = snapshot.docs.filter((d: any) => ['NOT_STARTED', 'IN_PROGRESS'].includes(d.data().status));
    if (activeDocs.length === 0) {
      callback(null);
    } else {
      const doc = activeDocs[0];
      callback({ id: doc.id, ...doc.data() } as BusJourney);
    }
  });
};

export const createBusJourney = async (journey: Omit<BusJourney, 'id'>) => {
  const newRef = doc(collection(db, 'bus_journeys'));
  await setDoc(newRef, { ...journey, createdAt: new Date().toISOString() });
  return newRef.id;
};

export const updateBusJourney = async (journeyId: string, data: Partial<BusJourney>) => {
  const docRef = doc(db, 'bus_journeys', journeyId);
  await updateDoc(docRef, data);
};

const ATTENDANCE_COLLECTION = 'attendance';

export const getAttendanceByDate = async (date: string): Promise<any[]> => {
  try {
    const q = query(collection(db, ATTENDANCE_COLLECTION), where('date', '==', date));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() } as any));
  } catch (e) {
    console.error("Error getting attendance", e);
    return [];
  }
};

export const subscribeAttendanceByDate = (date: string, callback: (records: any[]) => void) => {
  const q = query(collection(db, ATTENDANCE_COLLECTION), where('date', '==', date));
  return onSnapshot(q, (snapshot) => {
    const records = snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() } as any));
    callback(records);
  }, (error) => {
    console.error("Error subscribing to attendance", error);
  });
};

export const markAttendance = async (
  date: string, 
  studentId: string, 
  studentName: string, 
  type: 'IN' | 'OUT', 
  time: string,
  isStaff?: boolean,
  staffRole?: string
): Promise<void> => {
  try {
    const docId = `${date}_${studentId}`;
    const docRef = doc(db, ATTENDANCE_COLLECTION, docId);
    
    if (type === 'IN') {
      await setDoc(docRef, {
        id: docId,
        date,
        studentId,
        studentName,
        inTime: time,
        status: 'PRESENT',
        ...(isStaff !== undefined ? { isStaff } : {}),
        ...(staffRole !== undefined ? { staffRole } : {})
      }, { merge: true });
    } else if (type === 'OUT') {
      await setDoc(docRef, {
        id: docId,
        date,
        studentId,
        studentName,
        outTime: time,
        ...(isStaff !== undefined ? { isStaff } : {}),
        ...(staffRole !== undefined ? { staffRole } : {})
      }, { merge: true });
    }
  } catch (e) {
    console.error("Error marking attendance", e);
    throw e;
  }
};

export const clearAttendance = async (date: string, studentId: string): Promise<void> => {
  try {
    const docId = `${date}_${studentId}`;
    await deleteDoc(doc(db, ATTENDANCE_COLLECTION, docId));

    // Clear any legacy randomized ID doc for compatibility
    const q = query(collection(db, ATTENDANCE_COLLECTION), where('date', '==', date), where('studentId', '==', studentId));
    const snapshot = await getDocs(q);
    for (const docSnap of snapshot.docs) {
      if (docSnap.id !== docId) {
        await deleteDoc(doc(db, ATTENDANCE_COLLECTION, docSnap.id));
      }
    }
  } catch (e) {
    console.error("Error clearing attendance", e);
    throw e;
  }
};

export const getMergedRepeaterMembers = (survey: any, questions: any[]): any[] => {
  const arrayKeys = Object.keys(survey).filter(key => Array.isArray((survey as any)[key]));
  if (arrayKeys.length === 0) return [];

  let nameArrayKey = '';
  for (const key of arrayKeys) {
    const arr = survey[key] || [];
    if (arr.some((m: any) => getMemberName(m, questions))) {
      nameArrayKey = key;
      break;
    }
  }

  if (!nameArrayKey) {
    let fallback: any[] = [];
    for (const key of arrayKeys) {
      fallback = [...fallback, ...(survey[key] || [])];
    }
    return fallback;
  }

  const mainArr = survey[nameArrayKey] || [];
  return mainArr.map((member: any, idx: number) => {
    let mergedMember = { ...member };
    for (const key of arrayKeys) {
      if (key === nameArrayKey) continue;
      const otherArr = survey[key] || [];
      if (otherArr[idx] && typeof otherArr[idx] === 'object') {
        mergedMember = { ...mergedMember, ...otherArr[idx] };
      }
    }
    return mergedMember;
  });
};

export const extractStudentsFromSurvey = (survey: SurveyData, questions: any[]): any[] => {
  let allStudents: any[] = [];
  
  const isAdultOrNonStudent = (member: any): boolean => {
    if (!member || typeof member !== 'object') return true;
    for (const key of Object.keys(member)) {
      const val = member[key];
      if (typeof val === 'string' && val.trim().length > 0) {
        // Skip base64 images, photos, and extremely long fields
        if (val.startsWith('data:image/') || val.length > 100) continue;
        const v = val.toLowerCase().trim();
        const isParentOrSpouseWord = 
          v.includes('father') || v.includes('mother') || v.includes('parent') || v.includes('guardian') ||
          v.includes('spouse') || v.includes('wife') || v.includes('husband') || v.includes('self') || v.includes('head') ||
          v.includes('പിതാവ്') || v.includes('പിതാ') || v.includes('അച്ഛൻ') || v.includes('ഉപ്പ') ||
          v.includes('മാതാവ്') || v.includes('മാതാ') || v.includes('അമ്മ') || v.includes('ഉമ്മ') ||
          v.includes('ഭാര്യ') || v.includes('ഭർത്താവ്') || v.includes('രക്ഷിതാവ്') || v.includes('രക്ഷിതാ') ||
          v.includes('രക്ഷിതാവിന്റെ') || v.includes('നാഥൻ') || v.includes('നാഥ') || v.includes('സ്വയം');
          
        if (isParentOrSpouseWord) {
          const k = key.toLowerCase();
          if (!k.includes('name') && !k.includes('പേര്') && !k.includes('job') && !k.includes('work') && !k.includes('mobile') && !k.includes('phone') && !k.includes('email') && !k.includes('whatsapp') && !k.includes('നമ്പർ') && !k.includes('education') && !k.includes('qualification') && !k.includes('വിദ്യാഭ്യാസം')) {
            return true;
          }
        }
      }
    }
    return false;
  };

  const flatData = Object.keys(survey).reduce((acc: any, key) => {
    if (['id', 'submittedBy', 'submittedAt', 'paymentStatus', 'paymentAmount', 'paidBy', 'paidAt'].includes(key)) return acc;
    const val = (survey as any)[key];
    if (Array.isArray(val)) return acc;
    if (typeof val === 'object' && val !== null) {
      return { ...acc, ...val };
    }
    return { ...acc, [key]: val };
  }, {});

  const studentName = getMemberName(flatData, questions);
  
  // Extract parent fields to attach to students
  const fatherPhone = flatData['1782311944131'] || '';
  const motherPhone = flatData['1782311987788'] || '';
  const fatherName = flatData['1782311895371'] || '';
  const motherName = flatData['1782311923394'] || '';
  const hasMembership = flatData['1782312069160'];
  const membershipId = flatData['1782312123373'] || '';

  if (studentName) {
    const isPrimaryAdult = isAdultOrNonStudent(flatData);
    const primaryAge = getMemberAge(flatData, questions);
    const primaryCategory = getCategoryForAge(primaryAge);
    
    const primaryNameLower = studentName.toLowerCase();
    const primaryIsHobbyWord = primaryNameLower.includes('hobby') || primaryNameLower.includes('hobbies') || primaryNameLower.includes('വിനോദം') || primaryNameLower.includes('sports') || primaryNameLower.includes('art') || primaryNameLower.includes('football') || primaryNameLower.includes('cricket') || primaryNameLower.includes('reading') || primaryNameLower.includes('drawing');
    const isPrimaryHobby = primaryIsHobbyWord && !primaryNameLower.includes('name') && !primaryNameLower.includes('പേര്');

    if (!isPrimaryAdult && primaryCategory && !isPrimaryHobby) {
      allStudents.push({
        ...flatData,
        id: survey.id + '-main',
        studentName: studentName,
        serialNo: survey.sectionA?.serialNo || survey.surveyNo || survey.idNo || flatData.serialNo || flatData.idNo || 'PND',
        parentName: getHouseholdName(survey, questions),
        phone: getHouseholdPhone(survey, questions),
        fatherPhone,
        motherPhone,
        fatherName,
        motherName,
        hasMembership,
        membershipId,
        surveyId: survey.id
      });
    }
  }

  const repeaterMembers = getMergedRepeaterMembers(survey, questions);

  repeaterMembers.forEach((m: any, idx: number) => {
    const name = getMemberName(m, questions);
    if (name) {
      if (isAdultOrNonStudent(m)) return;
      
      const age = getMemberAge(m, questions);
      const category = getCategoryForAge(age);
      if (!category) return;
      
      const nameLower = name.toLowerCase();
      const isHobbyWord = nameLower.includes('hobby') || nameLower.includes('hobbies') || nameLower.includes('വിനോദം') || nameLower.includes('sports') || nameLower.includes('art') || nameLower.includes('football') || nameLower.includes('cricket') || nameLower.includes('reading') || nameLower.includes('drawing');
      if (isHobbyWord && !nameLower.includes('name') && !nameLower.includes('പേര്')) return;

      allStudents.push({
        ...m,
        id: survey.id + '-rep-' + idx,
        studentName: name,
        serialNo: m.serialNo || m.idNo || survey.sectionA?.serialNo || survey.serialNo || flatData.serialNo || 'PND',
        parentName: getHouseholdName(survey, questions),
        phone: getHouseholdPhone(survey, questions),
        fatherPhone,
        motherPhone,
        fatherName,
        motherName,
        hasMembership,
        membershipId,
        surveyId: survey.id
      });
    }
  });

  return allStudents;
};

const STAFF_COLLECTION = 'staff';

export const saveStaff = async (staff: StaffMember): Promise<StaffMember> => {
  await setDoc(doc(db, STAFF_COLLECTION, staff.id), staff);
  return staff;
};

export const getStaff = async (): Promise<StaffMember[]> => {
  try {
    const snapshot = await getDocs(collection(db, STAFF_COLLECTION));
    return snapshot.docs.map((d: any) => d.data() as StaffMember);
  } catch (error) {
    console.error("Failed to fetch staff:", error);
    return [];
  }
};

export const deleteStaff = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, STAFF_COLLECTION, id));
};

export const getNextStaffSerialNo = async (role: 'faculty' | 'coordinator'): Promise<string> => {
  try {
    const metaRef = doc(db, SETTINGS_COLLECTION, 'metadata');
    const metaSnap = await getDoc(metaRef);
    
    const prefix = role === 'faculty' ? 'FAC' : 'COOR';
    const fieldKey = role === 'faculty' ? 'lastFacultyNo' : 'lastCoordinatorNo';
    
    let lastNo = 0;
    if (metaSnap.exists() && typeof metaSnap.data()[fieldKey] === 'number') {
      lastNo = metaSnap.data()[fieldKey];
    } else {
      // scan existing staff to find max
      const staffList = await getStaff();
      const roleStaff = staffList.filter(s => s.role === role);
      roleStaff.forEach(s => {
        const numStr = s.serialNo.replace(`${prefix}-`, '');
        const num = parseInt(numStr, 10);
        if (!isNaN(num) && num > lastNo) lastNo = num;
      });
    }
    
    const nextNo = lastNo + 1;
    await setDoc(metaRef, { [fieldKey]: nextNo }, { merge: true });
    return `${prefix}-${nextNo.toString().padStart(4, '0')}`;
  } catch (e) {
    console.error("Failed to generate staff serial:", e);
    const rand = Math.floor(1000 + Math.random() * 9000);
    return role === 'faculty' ? `FAC-${rand}` : `COOR-${rand}`;
  }
};

export const saveTemplateByType = async (type: string, template: CardTemplate): Promise<void> => {
  const dataToSave = { ...template };
  if (dataToSave.photoConfig === undefined) {
    dataToSave.photoConfig = null;
  }
  await setDoc(doc(db, SETTINGS_COLLECTION, `template_${type}`), dataToSave);
};

export const getTemplateByType = async (type: string): Promise<CardTemplate | null> => {
  try {
    if (type === 'student') {
      const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'template_student'));
      if (docSnap.exists()) return docSnap.data() as CardTemplate;
      
      const legacySnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'template'));
      return legacySnap.exists() ? legacySnap.data() as CardTemplate : null;
    }
    
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, `template_${type}`));
    return docSnap.exists() ? docSnap.data() as CardTemplate : null;
  } catch (e: any) {
    const errorStr = String(e?.message || e).toLowerCase();
    const isOffline = errorStr.includes('offline') || e?.code === 'unavailable';
    if (isOffline) {
      console.log(`Firestore offline: getTemplateByType for ${type} deferred.`);
    } else {
      console.warn(`Failed to get template for ${type}:`, e);
    }
    return null;
  }
};
